import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ArrowUpDown, ReceiptText, Search, UserRound } from 'lucide-react';
import { useToast } from '../../Common/context/ToastContext';
import {
  getCpCustomerList,
  getCpManualPayments,
  getCpTerminalList,
  updateCpCustomerTerminal,
} from '../api/cp';

dayjs.extend(utc);

const CUSTOMER_BATCH_SIZE = 500;
const PAYMENT_LIMIT = 100;

function extractRows(data) {
  const rows = data?.rows ?? data?.list ?? (Array.isArray(data) ? data : []);
  return Array.isArray(rows) ? rows : [];
}

function normalizeTerminalList(data) {
  return extractRows(data).map((row, index) => {
    const id = row.id ?? row.terminalPk ?? row.terminal_pk ?? row.terminal_id ?? index;
    return {
      id,
      value: String(id),
      name: row.name ?? row.terminalName ?? row.terminal_name ?? '',
      loginId: row.terminalId ?? row.loginId ?? row.login_id ?? row.code ?? '',
      status: String(row.status ?? 'ACTIVE').toUpperCase(),
    };
  });
}

function createTerminalDirectory(terminals) {
  const directory = new Map();
  terminals.forEach((terminal) => {
    [terminal.id, terminal.value, terminal.loginId].forEach((key) => {
      if (key !== undefined && key !== null && key !== '') directory.set(String(key), terminal);
    });
  });
  return directory;
}

function normalizeCustomerList(data, terminalDirectory, offset = 0) {
  return extractRows(data).map((row, index) => {
    const terminal = row.terminal ?? row.salesperson ?? row.driver ?? {};
    const driverText = typeof row.driver === 'string' ? row.driver : '';
    const customerId = row.customerId ?? row.customer_id ?? row.pharmacyId ?? row.pharmacy_id ?? row.id ?? '';
    const mappingId = row.mappingId ?? row.mapping_id ?? '';
    const terminalPk = row.terminalPk ?? row.terminal_pk ?? row.terminal_id ?? row.terminalId ?? terminal.id ?? '';
    const assignedTerminal = terminalDirectory.get(String(terminalPk));
    const driverName = row.driverName ?? row.driver_name ?? row.salespersonName ?? row.salesperson_name ?? row.terminalName ?? row.terminal_name ?? terminal.name ?? driverText ?? '';
    const driverLoginId = row.driverLoginId ?? row.driver_login_id ?? row.salespersonLoginId ?? row.salesperson_login_id ?? row.terminalLoginId ?? row.terminal_login_id ?? terminal.terminalId ?? terminal.terminal_id ?? '';
    const code = row.externalCustomerCode ?? row.external_customer_code ?? row.institutionCode ?? row.institution_code ?? row.code ?? '';
    const name = row.name ?? row.customerName ?? row.customer_name ?? row.pharmacyName ?? row.pharmacy_name ?? '';
    return {
      key: String(mappingId || customerId || `${code}-${name}-${offset + index}`),
      customerId,
      mappingId,
      terminalPk: assignedTerminal?.value ?? (terminalPk !== '' ? String(terminalPk) : ''),
      code,
      name,
      representative: row.representativeName ?? row.representative_name ?? row.representative ?? row.owner ?? '',
      businessNo: row.businessNo ?? row.business_no ?? '',
      phone: row.phone ?? row.pharmacyPhone ?? row.pharmacy_phone ?? row.tel ?? '',
      address: row.address ?? row.addr ?? '',
      status: String(row.status ?? 'ACTIVE').toUpperCase(),
      driverName: driverName || assignedTerminal?.name || '',
      driverLoginId: driverLoginId || assignedTerminal?.loginId || '',
    };
  });
}

function normalizePaymentList(data) {
  return extractRows(data)
    .filter((row) => String(row.status ?? '').toUpperCase() !== 'FAILED')
    .map((row, index) => {
      const statusRaw = String(row.status ?? '').toUpperCase();
      const status = statusRaw.includes('CANCEL')
        ? (statusRaw.includes('PENDING') ? '취소대기' : '취소')
        : (statusRaw === 'COMPLETED' || statusRaw.includes('APPROVE') || statusRaw.includes('SUCCESS') ? '승인' : (row.status || '-'));
      return {
        id: row.payment_transaction_id ?? row.paymentTransactionId ?? row.id ?? index,
        date: row.approved_at ?? row.approvedAt ?? row.created_at ?? row.createdAt ?? '',
        approvalNo: row.appr_no ?? row.apprNo ?? row.approval_no ?? row.approvalNumber ?? '',
        cardName: row.card_nm ?? row.cardNm ?? row.cardName ?? '-',
        amount: Number(row.amount ?? row.trdAmt ?? row.trd_amt ?? row.pg_trd_amt ?? 0),
        driverName: row.salesperson_name ?? row.salespersonName ?? row.driver_name ?? row.driverName ?? row.terminal_name ?? row.terminalName ?? '',
        status,
      };
    })
    .sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
}

function pharmacySortName(value) {
  return String(value ?? '').replace(/^\s*\d{5,}-\d+\s*/, '').trim();
}

function compareText(a, b) {
  return String(a ?? '').localeCompare(String(b ?? ''), 'ko-KR', { numeric: true, sensitivity: 'base' });
}

function formatPhone(value) {
  const numbers = String(value ?? '').replace(/[^0-9]/g, '');
  if (!numbers) return '-';
  if (numbers.startsWith('02')) {
    if (numbers.length <= 9) return numbers.replace(/^(02)(\d{3})(\d{4})$/, '$1-$2-$3');
    return numbers.replace(/^(02)(\d{4})(\d{4})$/, '$1-$2-$3');
  }
  return numbers.replace(/^(\d{3})(\d{3,4})(\d{4})$/, '$1-$2-$3');
}

function formatPaymentDate(value) {
  if (!value) return '-';
  const date = dayjs.utc(value);
  return date.isValid() ? date.add(9, 'hour').format('YYYY.MM.DD HH:mm') : String(value);
}

function formatWon(value) {
  return `${Number(value || 0).toLocaleString('ko-KR')}원`;
}

function PharmacyAssignmentManage() {
  const { showToast } = useToast();
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [terminals, setTerminals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [payments, setPayments] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [firstData, terminalData] = await Promise.all([
          getCpCustomerList({ page: 1, limit: CUSTOMER_BATCH_SIZE }),
          getCpTerminalList({ page: 1, limit: 500 }),
        ]);
        if (cancelled) return;

        const terminalList = normalizeTerminalList(terminalData).sort((a, b) => (
          compareText(a.name || a.loginId, b.name || b.loginId)
        ));
        const terminalDirectory = createTerminalDirectory(terminalList);
        const firstRows = extractRows(firstData);
        const nextTotal = Number(firstData?.total ?? firstData?.count) || firstRows.length;
        const effectivePageSize = firstRows.length || CUSTOMER_BATCH_SIZE;
        const pageCount = Math.max(1, Math.ceil(nextTotal / effectivePageSize));
        const remainingData = pageCount > 1
          ? await Promise.all(Array.from({ length: pageCount - 1 }, (_, index) => (
            getCpCustomerList({ page: index + 2, limit: CUSTOMER_BATCH_SIZE })
          )))
          : [];
        if (cancelled) return;

        const normalized = [firstData, ...remainingData].flatMap((data, pageIndex) => (
          normalizeCustomerList(data, terminalDirectory, pageIndex * effectivePageSize)
        ));
        const uniqueRows = Array.from(new Map(normalized.map((row) => [row.key, row])).values());
        setTerminals(terminalList);
        setRows(uniqueRows);
        setTotal(nextTotal || uniqueRows.length);
      } catch (error) {
        if (!cancelled) {
          setRows([]);
          setTotal(0);
          setTerminals([]);
          setSelected(null);
          showToast(error?.message || '약국 목록을 불러오지 못했습니다.', 'error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleRows = useMemo(() => {
    const keyword = query.trim().toLocaleLowerCase('ko-KR');
    const filtered = keyword
      ? rows.filter((row) => [
        row.code,
        row.name,
        pharmacySortName(row.name),
        row.representative,
        row.businessNo,
        row.phone,
        row.address,
        row.driverName,
        row.driverLoginId,
      ].some((value) => String(value || '').toLocaleLowerCase('ko-KR').includes(keyword)))
      : rows;
    return [...filtered].sort((a, b) => {
      if (sortBy === 'code') return compareText(a.code, b.code) || compareText(pharmacySortName(a.name), pharmacySortName(b.name));
      return compareText(pharmacySortName(a.name), pharmacySortName(b.name)) || compareText(a.code, b.code);
    });
  }, [query, rows, sortBy]);

  useEffect(() => {
    setSelected((previous) => {
      if (previous && visibleRows.some((row) => row.key === previous.key)) {
        return visibleRows.find((row) => row.key === previous.key);
      }
      return visibleRows[0] ?? null;
    });
  }, [visibleRows]);

  useEffect(() => {
    if (!selected?.name) {
      setPayments([]);
      return undefined;
    }
    let cancelled = false;
    const loadPayments = async () => {
      setPaymentLoading(true);
      try {
        const data = await getCpManualPayments({ page: 1, limit: PAYMENT_LIMIT, q: selected.name });
        if (!cancelled) setPayments(normalizePaymentList(data));
      } catch (error) {
        if (!cancelled) {
          setPayments([]);
          showToast(error?.message || '약국 결제내역을 불러오지 못했습니다.', 'error');
        }
      } finally {
        if (!cancelled) setPaymentLoading(false);
      }
    };
    loadPayments();
    return () => { cancelled = true; };
  }, [selected?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateSelectedRow = useCallback((updatedRow) => {
    setRows((current) => current.map((row) => (row.key === updatedRow.key ? updatedRow : row)));
    setSelected((current) => (current?.key === updatedRow.key ? updatedRow : current));
  }, []);

  const changeTerminal = async (row, nextValue) => {
    if (!row.customerId || savingKey) return;
    const nextTerminal = terminals.find((terminal) => terminal.value === nextValue) ?? null;
    if (String(row.terminalPk || '') === String(nextTerminal?.value || '')) return;

    setSavingKey(row.key);
    try {
      await updateCpCustomerTerminal({
        customerId: row.customerId,
        terminalId: nextTerminal?.id ?? null,
      });
      updateSelectedRow({
        ...row,
        terminalPk: nextTerminal?.value ?? '',
        driverName: nextTerminal?.name ?? '',
        driverLoginId: nextTerminal?.loginId ?? '',
      });
      showToast(`${row.name || '약국'} 담당자를 변경했습니다.`, 'success');
    } catch (error) {
      showToast(error?.message || '담당자 변경에 실패했습니다.', 'error');
    } finally {
      setSavingKey('');
    }
  };

  const approvedPayments = payments.filter((payment) => payment.status === '승인');
  const approvedAmount = approvedPayments.reduce((sum, payment) => sum + payment.amount, 0);
  const resultCountLabel = query.trim() ? `${visibleRows.length.toLocaleString('ko-KR')}곳 검색됨` : `${total.toLocaleString('ko-KR')}곳`;

  return (
    <div className="flex-1 min-h-0 overflow-hidden bg-[#f7f8fa] px-6 py-4">
      <div className="h-full min-h-0 flex flex-col gap-3">
        <header className="shrink-0 flex items-end justify-between gap-4">
          <div>
            <h1 className="text-[20px] font-extrabold text-[#191f28]">약국 관리</h1>
            <p className="mt-1 text-[13px] text-[#6b7684]">약국 정보를 한눈에 확인하고 담당 영업자를 바로 변경합니다.</p>
          </div>
          <span className="text-[13px] font-semibold text-[#6b7684]">총 {resultCountLabel}</span>
        </header>

        <div className="shrink-0 flex flex-wrap items-center justify-between gap-3">
          <div className="h-[40px] w-[420px] max-w-full flex items-center gap-2 rounded-lg border border-[#e5e8eb] bg-white px-3 focus-within:border-[var(--brand-primary)]">
            <Search size={17} className="shrink-0 text-[#8b95a1]" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="약국명, 거래처코드, 사업자번호, 담당자 검색"
              className="min-w-0 flex-1 border-0 bg-transparent text-[14px] text-[#191f28] outline-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <ArrowUpDown size={15} className="text-[#8b95a1]" />
            <div className="flex rounded-lg border border-[#e5e8eb] bg-white p-0.5">
              <button
                type="button"
                aria-pressed={sortBy === 'name'}
                onClick={() => setSortBy('name')}
                className={`rounded-md px-3 py-1.5 text-[12px] font-bold ${sortBy === 'name' ? 'bg-[#191f28] text-white' : 'text-[#6b7684] hover:bg-[#f2f4f6]'}`}
              >
                약국명 가나다순
              </button>
              <button
                type="button"
                aria-pressed={sortBy === 'code'}
                onClick={() => setSortBy('code')}
                className={`rounded-md px-3 py-1.5 text-[12px] font-bold ${sortBy === 'code' ? 'bg-[#191f28] text-white' : 'text-[#6b7684] hover:bg-[#f2f4f6]'}`}
              >
                거래처코드순
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 flex gap-4">
          <section className="basis-[66%] min-w-0 overflow-hidden rounded-xl border border-[#e5e8eb] bg-white flex flex-col">
            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full min-w-[1300px] text-left text-[12px]">
                <thead className="sticky top-0 z-10 bg-[#f9fafb] text-[#6b7684]">
                  <tr className="h-[38px] border-b border-[#e5e8eb]">
                    <th className="w-[105px] px-3">거래처코드</th>
                    <th className="min-w-[220px] px-3">약국명</th>
                    <th className="w-[95px] px-3">대표자</th>
                    <th className="w-[120px] px-3">사업자번호</th>
                    <th className="w-[125px] px-3">연락처</th>
                    <th className="min-w-[210px] px-3">주소</th>
                    <th className="w-[190px] px-3">담당 영업자</th>
                    <th className="w-[64px] px-3 text-center">상태</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f3f5]">
                  {loading ? (
                    <tr><td colSpan={8} className="px-4 py-14 text-center text-[#8b95a1]">전체 약국 목록을 불러오는 중입니다.</td></tr>
                  ) : visibleRows.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-14 text-center text-[#8b95a1]">조회된 약국이 없습니다.</td></tr>
                  ) : visibleRows.map((row) => {
                    const currentValue = terminals.some((terminal) => terminal.value === String(row.terminalPk)) ? String(row.terminalPk) : '';
                    const isSelected = selected?.key === row.key;
                    return (
                      <tr
                        key={row.key}
                        tabIndex={0}
                        onClick={() => setSelected(row)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setSelected(row);
                          }
                        }}
                        className={`h-[44px] cursor-pointer outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--brand-primary)] ${isSelected ? 'bg-[var(--brand-primary-soft)]' : 'hover:bg-[#f9fafb]'}`}
                      >
                        <td className="whitespace-nowrap px-3 font-mono text-[#4e5968]">{row.code || '-'}</td>
                        <td className="max-w-[280px] truncate px-3 font-bold text-[#191f28]" title={row.name || '-'}>{row.name || '-'}</td>
                        <td className="truncate px-3 text-[#4e5968]" title={row.representative || '-'}>{row.representative || '-'}</td>
                        <td className="whitespace-nowrap px-3 text-[#4e5968]">{row.businessNo || '-'}</td>
                        <td className="whitespace-nowrap px-3 text-[#4e5968]">{formatPhone(row.phone)}</td>
                        <td className="max-w-[260px] truncate px-3 text-[#6b7684]" title={row.address || '-'}>{row.address || '-'}</td>
                        <td className="px-2" onClick={(event) => event.stopPropagation()}>
                          <div className="relative">
                            <label className="sr-only" htmlFor={`terminal-${row.key}`}>{row.name} 담당 영업자</label>
                            <select
                              id={`terminal-${row.key}`}
                              value={currentValue}
                              disabled={savingKey === row.key || !row.customerId}
                              onChange={(event) => changeTerminal(row, event.target.value)}
                              className="h-[32px] w-full rounded-md border border-[#dfe3e6] bg-white px-2 pr-7 text-[12px] font-semibold text-[#333d4b] outline-none focus:border-[var(--brand-primary)] disabled:cursor-wait disabled:opacity-60"
                            >
                              <option value="">미할당</option>
                              {terminals.map((terminal) => (
                                <option key={terminal.value} value={terminal.value} disabled={terminal.status !== 'ACTIVE' && terminal.value !== currentValue}>
                                  {terminal.name || terminal.loginId || '-'}{terminal.loginId ? ` (${terminal.loginId})` : ''}{terminal.status !== 'ACTIVE' ? ' · 비활성' : ''}
                                </option>
                              ))}
                            </select>
                            {savingKey === row.key && <span className="pointer-events-none absolute inset-y-0 right-7 flex items-center text-[10px] text-[#8b95a1]">저장 중</span>}
                          </div>
                        </td>
                        <td className="px-3 text-center">
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${row.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                            {row.status === 'ACTIVE' ? '활성' : '비활성'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="basis-[34%] min-w-[390px] overflow-hidden rounded-xl border border-[#e5e8eb] bg-white flex flex-col">
            {!selected ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 text-[#adb5bd]">
                <ReceiptText size={32} strokeWidth={1.7} />
                <p className="text-[14px] font-bold">약국을 선택하면 결제내역이 표시됩니다.</p>
              </div>
            ) : (
              <>
                <div className="shrink-0 border-b border-[#eef1f4] px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-[12px] font-bold text-[var(--brand-primary)]">
                        <UserRound size={14} /> {selected.driverName || selected.driverLoginId || '미할당'}
                      </div>
                      <h2 className="mt-1 truncate text-[17px] font-extrabold text-[#191f28]" title={selected.name}>{selected.name || '-'}</h2>
                      <p className="mt-0.5 font-mono text-[11px] text-[#8b95a1]">거래처코드 {selected.code || '-'}</p>
                    </div>
                    <span className={`shrink-0 rounded px-2 py-1 text-[10px] font-bold ${selected.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {selected.status === 'ACTIVE' ? '활성' : '비활성'}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <SummaryCard label="최근 결제" value={`${payments.length.toLocaleString('ko-KR')}건`} />
                    <SummaryCard label="승인 건수" value={`${approvedPayments.length.toLocaleString('ko-KR')}건`} />
                    <SummaryCard label="승인 합계" value={formatWon(approvedAmount)} accent />
                  </div>
                </div>

                <div className="shrink-0 flex items-center justify-between border-b border-[#eef1f4] px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <ReceiptText size={16} className="text-[#4e5968]" />
                    <h3 className="text-[14px] font-extrabold text-[#191f28]">최근 결제내역</h3>
                  </div>
                  <span className="text-[11px] text-[#8b95a1]">최대 {PAYMENT_LIMIT}건</span>
                </div>

                <div className="flex-1 min-h-0 overflow-auto">
                  <table className="w-full min-w-[520px] text-[11px]">
                    <thead className="sticky top-0 z-10 bg-[#f9fafb] text-[#6b7684]">
                      <tr className="h-[34px] border-b border-[#eef1f4]">
                        <th className="w-[132px] px-3 text-left">결제일</th>
                        <th className="px-3 text-left">승인번호 / 카드사</th>
                        <th className="w-[100px] px-3 text-right">금액</th>
                        <th className="w-[58px] px-3 text-center">상태</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#f1f3f5]">
                      {paymentLoading ? (
                        <tr><td colSpan={4} className="px-3 py-12 text-center text-[#8b95a1]">결제내역을 불러오는 중입니다.</td></tr>
                      ) : payments.length === 0 ? (
                        <tr><td colSpan={4} className="px-3 py-12 text-center text-[#8b95a1]">이 약국의 결제내역이 없습니다.</td></tr>
                      ) : payments.map((payment) => {
                        const canceled = String(payment.status).includes('취소');
                        return (
                          <tr key={payment.id} className={canceled ? 'bg-red-50/70' : 'hover:bg-[#f9fafb]'}>
                            <td className="whitespace-nowrap px-3 py-2 text-[#4e5968]">{formatPaymentDate(payment.date)}</td>
                            <td className="px-3 py-2">
                              <p className="font-semibold text-[#333d4b]">{payment.approvalNo || '-'}</p>
                              <p className="mt-0.5 truncate text-[#8b95a1]">{payment.cardName || '-'}{payment.driverName ? ` · ${payment.driverName}` : ''}</p>
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-right font-bold tabular-nums text-[#191f28]">{formatWon(payment.amount)}</td>
                            <td className={`whitespace-nowrap px-3 py-2 text-center font-bold ${canceled ? 'text-red-600' : 'text-emerald-700'}`}>{payment.status}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent = false }) {
  return (
    <div className="min-w-0 rounded-lg bg-[#f7f8fa] px-3 py-2">
      <span className="block text-[10px] font-semibold text-[#8b95a1]">{label}</span>
      <strong className={`mt-0.5 block truncate text-[12px] ${accent ? 'text-[var(--brand-primary)]' : 'text-[#333d4b]'}`} title={value}>{value}</strong>
    </div>
  );
}

export default PharmacyAssignmentManage;
